import { useState, useCallback, useEffect, useRef } from "react";
import type { Schedule } from "../types";
import {
  timeToMinutes,
  minutesToTime,
  pixelToMinutesHorizontal,
  clampStartTime,
  clampEndTime,
  clampDragPosition,
  snapToGrid,
} from "../utils";

// Width of the day label column (w-20 = 80px) - must be subtracted from grid calculations
const DAY_LABEL_WIDTH = 80;

type ResizeState = { scheduleId: string; edge: "left" | "right" };
type DragState = { scheduleId: string; offsetMinutes: number };

type UseGridDragOptions = {
  schedules: Schedule[];
  onScheduleUpdate: (id: string, updates: Partial<Schedule>) => void;
  onSelect: (id: string) => void;
};

// Hook for handling drag and resize interactions on the schedule grid (horizontal layout)
export const useGridDrag = ({ schedules, onScheduleUpdate, onSelect }: UseGridDragOptions) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [resizing, setResizing] = useState<ResizeState | null>(null);
  const [dragging, setDragging] = useState<DragState | null>(null);

  const isInteracting = resizing !== null || dragging !== null;

  // Start resizing a schedule block
  const startResize = useCallback(
    (scheduleId: string, edge: "left" | "right") => {
      setResizing({ scheduleId, edge });
      onSelect(scheduleId);
    },
    [onSelect],
  );

  // Start dragging a schedule block
  // offsetMinutes = how far the click was from the block's left edge (so it doesn't snap to cursor)
  const startDrag = useCallback(
    (scheduleId: string, clickX: number) => {
      if (!gridRef.current) return;

      const schedule = schedules.find((s) => s.id === scheduleId);
      if (!schedule) return;

      const rect = gridRef.current.getBoundingClientRect();
      // Subtract day label width from position and total width to get schedule area coordinates
      const scheduleAreaWidth = rect.width - DAY_LABEL_WIDTH;
      const clickMinutes = pixelToMinutesHorizontal(clickX - rect.left - DAY_LABEL_WIDTH, scheduleAreaWidth);
      const startMinutes = timeToMinutes(schedule.startTime);
      const offsetMinutes = clickMinutes - startMinutes;

      setDragging({ scheduleId, offsetMinutes });
      onSelect(scheduleId);
    },
    [schedules, onSelect],
  );

  // Stop any interaction
  const stopInteraction = useCallback(() => {
    setResizing(null);
    setDragging(null);
  }, []);

  // Handle mouse movement during resize
  const handleResizeMove = useCallback(
    (minutes: number) => {
      if (!resizing) return;

      const schedule = schedules.find((s) => s.id === resizing.scheduleId);
      if (!schedule) return;

      if (resizing.edge === "left") {
        const endMins = timeToMinutes(schedule.endTime);
        const newStart = clampStartTime(minutes, endMins);
        onScheduleUpdate(resizing.scheduleId, { startTime: minutesToTime(newStart) });
      } else {
        const startMins = timeToMinutes(schedule.startTime);
        const newEnd = clampEndTime(minutes, startMins);
        onScheduleUpdate(resizing.scheduleId, { endTime: minutesToTime(newEnd) });
      }
    },
    [resizing, schedules, onScheduleUpdate],
  );

  // Handle mouse movement during drag
  const handleDragMove = useCallback(
    (minutes: number) => {
      if (!dragging) return;

      const schedule = schedules.find((s) => s.id === dragging.scheduleId);
      if (!schedule) return;

      const duration = timeToMinutes(schedule.endTime) - timeToMinutes(schedule.startTime);
      const rawStart = snapToGrid(minutes - dragging.offsetMinutes);
      const newStart = clampDragPosition(rawStart, duration);
      const newEnd = newStart + duration;

      onScheduleUpdate(dragging.scheduleId, {
        startTime: minutesToTime(newStart),
        endTime: minutesToTime(Math.min(1439, newEnd)),
      });
    },
    [dragging, schedules, onScheduleUpdate],
  );

  // Global mouse move handler (horizontal layout - uses X axis)
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!gridRef.current) return;

      const rect = gridRef.current.getBoundingClientRect();
      // Subtract day label width from position and total width to get schedule area coordinates
      const scheduleAreaWidth = rect.width - DAY_LABEL_WIDTH;
      const relativeX = Math.max(0, Math.min(e.clientX - rect.left - DAY_LABEL_WIDTH, scheduleAreaWidth));
      const minutes = pixelToMinutesHorizontal(relativeX, scheduleAreaWidth);

      if (resizing) {
        handleResizeMove(minutes);
      } else if (dragging) {
        handleDragMove(minutes);
      }
    },
    [resizing, dragging, handleResizeMove, handleDragMove],
  );

  // Set up global event listeners during interaction
  useEffect(() => {
    if (!isInteracting) return;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopInteraction);
    document.body.style.userSelect = "none";
    document.body.style.cursor = resizing ? (resizing.edge === "left" ? "w-resize" : "e-resize") : "grabbing";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopInteraction);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isInteracting, handleMouseMove, stopInteraction, resizing]);

  return {
    gridRef,
    isInteracting,
    isDragging: (scheduleId: string) => dragging?.scheduleId === scheduleId,
    startResize,
    startDrag,
    stopInteraction,
  };
};
